import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/hooks/useLanguage';
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface ConsentStepProps {
  form: UseFormReturn<any>;
}

const ConsentStep = ({ form }: ConsentStepProps) => {
  const { t, language } = useLanguage();

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold mb-2">
          {language === 'es' ? 'Términos y Consentimiento' : language === 'vi' ? 'Điều khoản & Đồng ý' : 'Terms & Consent'}
        </h3>
        <p className="text-muted-foreground">
          {language === 'es' 
            ? 'Por favor revise y acepte los términos a continuación'
            : language === 'vi'
            ? 'Vui lòng xem xét và chấp nhận các điều khoản dưới đây'
            : 'Please review and accept the terms below'}
        </p>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-semibold mb-2">
            {language === 'es' ? 'Aviso Importante' : language === 'vi' ? 'Thông báo quan trọng' : 'Important Notice'}
          </p>
          <p className="text-sm">
            {language === 'es'
              ? 'Esta consulta no es reembolsable y cuesta $150. Enviar este formulario no crea una relación abogado-cliente hasta que se firme un acuerdo formal. Por favor traiga todos los documentos relevantes a su consulta.'
              : language === 'vi'
              ? 'Buổi tư vấn này không hoàn lại và có phí $150. Gửi biểu mẫu này không tạo mối quan hệ luật sư-khách hàng cho đến khi ký hợp đồng chính thức. Vui lòng mang tất cả tài liệu liên quan đến buổi tư vấn.'
              : 'This consultation is non-refundable and costs $150. Submitting this form does not create an attorney-client relationship until a formal agreement is signed. Please bring all relevant documents to your consultation.'}
          </p>
        </AlertDescription>
      </Alert>

      <FormField
        control={form.control}
        name="consentNotifications"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer">
                {t('booking.consentNotifications')}
              </FormLabel>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="marketingConsent"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer">
                {language === 'es' 
                  ? 'Acepto recibir comunicaciones de marketing'
                  : language === 'vi'
                  ? 'Tôi đồng ý nhận thông tin tiếp thị'
                  : 'I agree to receive marketing communications'}
              </FormLabel>
              <FormDescription className="text-xs">
                {language === 'es'
                  ? 'Puede darse de baja en cualquier momento. No es obligatorio.'
                  : language === 'vi'
                  ? 'Bạn có thể hủy đăng ký bất cứ lúc nào. Không bắt buộc.'
                  : 'You can unsubscribe at any time. This is optional.'}
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="consentTerms"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer">
                {t('booking.consentTerms')} *
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />

      <div className="bg-muted p-4 rounded-lg">
        <p className="text-sm text-center text-muted-foreground">
          {language === 'es'
            ? 'Por favor traiga todos sus documentos y sus preguntas para esta consulta. Esta consulta no es reembolsable.'
            : language === 'vi'
            ? 'Vui lòng mang tất cả tài liệu và chuẩn bị câu hỏi. Buổi tư vấn này không hoàn lại.'
            : 'Please bring all documents and be prepared with all your questions. This consultation is non-refundable.'}
        </p>
      </div>
    </div>
  );
};

export default ConsentStep;
